import { type FunctionComponent, useContext, useEffect, useState } from 'react';
import { DEFAULT_SWATCH_GROUP_NUM, SwatchContext } from '../../../contexts/product-card/swatch.context';
import { type TrackedEvent } from '../../../helpers/analytics/event-types';
import { getSwatchesToDisplay, getTotalGroups } from '../../../helpers/product-helper/product-swatch/product-swatch.helper';
import { useTrackEvent } from '../../../hooks/analytics/analytics.hooks';
import { type ProductSwatch } from '../../../types/product.types';
import { TextButton } from '../../buttons';
import ChevronLeftIcon from '../../svgs/untitled-ui/chevron-left.svg';
import ChevronRightIcon from '../../svgs/untitled-ui/chevron-right.svg';
import { DEFAULT_SWATCH_SIZE, ProductCardSwatch } from '../product-card-swatch/product-card-swatch.component';

type SwatchRowProps = {
	swatches: ProductSwatch[];
	className?: string;
	swatchSize?: number;
	selectEvent?: TrackedEvent;
};

const SwatchRow: FunctionComponent<SwatchRowProps> = ({ swatches, className = '', swatchSize = DEFAULT_SWATCH_SIZE, selectEvent }) => (
	<div className={className} data-testid={`swatches-${swatchSize}`}>
		{swatches.map((swatch) => (
			<ProductCardSwatch
				key={swatch.variantId.toString()}
				swatch={swatch}
				className={`ma0`}
				swatchSize={swatchSize}
				selectEvent={selectEvent}
			/>
		))}
	</div>
);

export type ProductCardSwatchSelectorProps = {
	swatches: ProductSwatch[];
	maxNumberToDisplay: number;
	isTileView: boolean;
	scrollEvent: TrackedEvent;
	selectEvent?: TrackedEvent;
	allowSingleSwatch?: boolean;
};

export const ProductCardSwatchSelector: FunctionComponent<ProductCardSwatchSelectorProps> = ({
	swatches,
	maxNumberToDisplay,
	isTileView,
	scrollEvent,
	selectEvent,
	allowSingleSwatch
}) => {
	const { swatchGroupDisplayed, setSwatchGroupDisplayed } = useContext(SwatchContext);
	const [userAlreadyScrolled, setUserAlreadyScrolled] = useState(false);
	const trackEvent = useTrackEvent();

	useEffect(() => {
		setSwatchGroupDisplayed(DEFAULT_SWATCH_GROUP_NUM);
	}, [isTileView, setSwatchGroupDisplayed]);

	const totalGroups = getTotalGroups(swatches.length, maxNumberToDisplay);
	const showLeftArrow = swatchGroupDisplayed > 1;
	const showRightArrow = totalGroups !== swatchGroupDisplayed;

	if (!allowSingleSwatch && swatches.length === 1) {
		return null;
	}

	const slicedSwatches = getSwatchesToDisplay(swatches, maxNumberToDisplay, swatchGroupDisplayed);
	const arrowSize = `30px`;

	const onSwatchScroll = () => {
		if (userAlreadyScrolled) {
			return;
		}
		setUserAlreadyScrolled(true);
		trackEvent({ event: scrollEvent });
	};

	const handleArrowClick = (increment: number) => {
		setSwatchGroupDisplayed(swatchGroupDisplayed + increment);
		trackEvent({ event: scrollEvent });
	};

	return (
		<div className="flex" data-testid="product-card-swatch-selector" aria-label="Available finish options">
			<div className={`dn ${showLeftArrow ? 'flex-ns' : ''}`} style={{ width: arrowSize, height: arrowSize, marginRight: '.25rem' }}>
				<TextButton
					buttonStyle="PRIMARY"
					testId="left-swatch-arrow"
					disabled={swatchGroupDisplayed === 0}
					onClick={() => handleArrowClick(-1)}
					className="pa2"
					aria-label="Show previous finishes">
					<ChevronLeftIcon className="text-2xl -mt-1.5 -mr-1.5" />
				</TextButton>
			</div>
			<div className="overflow-x-auto overflow-x-visible-ns pb2 pb0-l" data-testid="swatch-scroll" onScroll={onSwatchScroll}>
				<SwatchRow swatches={swatches} className="flex dn-ns" selectEvent={selectEvent} />
				<SwatchRow swatches={slicedSwatches} className="dn flex-ns" swatchSize={22} selectEvent={selectEvent} />
			</div>
			<div className={`dn ${showRightArrow ? 'flex-ns' : ''}`} style={{ width: arrowSize, height: arrowSize }}>
				<TextButton
					testId="right-swatch-arrow"
					buttonStyle="PRIMARY"
					onClick={() => handleArrowClick(1)}
					className="pa2"
					aria-label="Show next finishes">
					<ChevronRightIcon className="text-2xl -mt-1.5 -ml-1.5" />
				</TextButton>
			</div>
		</div>
	);
};
